import get from "lodash/get";
import React from "react";
import { Button, DownloadIcon, Pagination, PlusIcon } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { useTranslation } from "react-i18next";
import { useHistory, useRouteMatch } from "react-router-dom";

import DealersHtmlFilesForm from "~/components/Forms/DealersHtmlFilesForm";
import ErrorBoundary from "~/HOC/ErrorBoundary";
import DealersFilter from "~/pages/Dealers/Filter";
import Modal from "~/shared/ui/Fields/Modal";
import Wrapper from "~/shared/ui/Wrapper";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { DealerSearch, useGetDealersQuery } from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useNotify } from "~/shared/hooks/useNotify";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2, useTableSort } from "~/shared/ui/TableV2";

import useGetDealersRegistryReport from "./hooks/useGetDealersRegistryReport";
import useSyncdataDealers from "./hooks/useSyncDataDealers";
import { mapDealerItem } from "./model/mapDealerItem";
import { getDealersColumns } from "./model/tableAdapter";
import { ARR_INCLUDE_EN, DEALER_OPTIONS } from "./options";

import s from "./Dealers.less";

// Числовые поля поиска (в схеме DealerSearch — Int)
const NUMERIC_FILTER_KEYS = ["Main", "extendedForNextYear"];

const Dealers = () => {
  const { t } = useTranslation();
  const { mutate: dealerSync } = useSyncdataDealers();
  const { mutate: downloadRegistryReport, isLoading: registryReportLoading } =
    useGetDealersRegistryReport();
  const { hasPermission } = usePermissions();
  const language = localStorage.getItem("language");
  const isEn = language === "en";
  const noCaching = sessionStorage.getItem("noCaching");
  const setData = useSetLocationData();
  const history = useHistory();
  const openAdd = () => {
    history.replace("/dealers/add");
  };

  const { count, page, openModal, sortField, sortType } = useGetLocationData();
  // Ключи фильтров приходят из URL и не описаны в типе useGetLocationData
  const {
    id,
    DealerName,
    INN,
    status,
    birCode,
    country,
    FO,
    subject,
    city,
    category,
    Main,
    extendedForNextYear,
  } = useGetLocationData() as Record<string, unknown>;

  const openPkdFiles = () => {
    history.push(`/reg_men/`);
  };
  const { params, path } = useRouteMatch();
  const idArchive = params["period"];

  const rawFilters: Record<string, unknown> = {
    id,
    DealerName,
    INN,
    status,
    birCode,
    country,
    FO,
    subject,
    city,
    category,
    Main,
    extendedForNextYear,
  };

  const toVariableKey = (key: string) =>
    isEn && ARR_INCLUDE_EN.includes(key) ? `${key}En` : key;

  const activeFilterKeys = Object.keys(rawFilters).filter(
    (key) => !!rawFilters[key],
  );

  const search: DealerSearch = activeFilterKeys.reduce<DealerSearch>(
    (acc, key) => ({
      ...acc,
      [toVariableKey(key)]: NUMERIC_FILTER_KEYS.includes(key)
        ? Number(rawFilters[key])
        : String(rawFilters[key]),
    }),
    { SearchFields: activeFilterKeys.map(toVariableKey) },
  );

  const { data, loading } = useGetDealersQuery({
    variables: {
      count,
      page,
      search,
      sortField,
      sortType,
      archivePeriodId: idArchive,
      doSearchArchive: !!idArchive,
    },
    fetchPolicy: noCaching ? "cache-and-network" : "cache-first",
  });

  const { notifyError } = useNotify();

  const ErrorBoundaryNotification = () => {
    notifyError(t("common.errorWeb"));
  };

  const handleSync = () => {
    dealerSync();
  };
  const documents = () => {
    history.push("/dealers/list-of-documents");
  };

  const dealers = get(data, "DealerGetter.getDealers.items", []).map((dealer) =>
    mapDealerItem(dealer, isEn, t, "reestrAddress"),
  );

  const total = get(data, "DealerGetter.getDealers.total", 0);

  const handleDealerCellClick = (
    record: { id?: string | number; ArchivePeriodId?: string | number },
    option: string,
  ) => {
    if (path !== "/dealers/inheritance/") {
      if (option === "DealerName") {
        history.push(`/dealers/${record.id}/`);
      }
    } else if (option === "id" && record.ArchivePeriodId) {
      history.push(`/dealers/period/${record.ArchivePeriodId}/${record.id}`);
    }
  };

  const sortableColumns = DEALER_OPTIONS.filter(
    (option) =>
      !["regionalServiceManager", "regionalSalesManager"].includes(option),
  );

  const columns = getDealersColumns(
    t,
    DEALER_OPTIONS,
    path,
    handleDealerCellClick,
    sortField,
    sortType,
    sortableColumns,
  );

  const {
    sortType: normalizedSortType,
    sortableColumns: normalizedSortableColumns,
    onSortChange,
  } = useTableSort({
    sortField,
    sortType,
    sortableColumns,
    setData,
  });

  const { handleCount, handlePage } = usePaginationParams();

  const handleCloseModal = () => {
    setData("openModal", "");
  };

  return (
    <Wrapper.Content
      title={t("dealers.dealer_register")}
      filterComponent={<DealersFilter />}
      controlComponent={
        <Flex gap={10}>
          {hasPermission("contracts.dealers") && (
            <Button onClick={documents} size={"middle"} color={"secondary"}>
              {t("ui.documents")}
            </Button>
          )}
          {hasPermission("dealer.create") && (
            <Button
              onClick={openAdd}
              size={"middle"}
              icon={<PlusIcon size={16} />}
            >
              {t("ui.add")}
            </Button>
          )}
        </Flex>
      }
    >
      <Flex justify={"space-between"} className={s.actionsRow}>
        <Flex gap={10}>
          {hasPermission("dealer.dmsImport") && (
            <Button
              color={"secondary"}
              variant={"filled"}
              bold
              fontSize="12"
              size={"middle"}
              radius={30}
              onClick={handleSync}
            >
              {t("ui.sync")}
            </Button>
          )}
          {hasPermission("dealer.registryReport") && (
            <Button
              color={"secondary"}
              variant={"filled"}
              bold
              fontSize="12"
              size={"middle"}
              radius={30}
              icon={<DownloadIcon size={16} fill="#495a64" />}
              onClick={() => downloadRegistryReport()}
              disabled={registryReportLoading}
            >
              {t("dealers.registryReport")}
            </Button>
          )}
          {hasPermission("users.getRegMens") && (
            <Button
              color={"secondary"}
              variant={"filled"}
              bold
              fontSize="12"
              size={"middle"}
              radius={30}
              onClick={openPkdFiles}
            >
              {t("regMen.btnRegMenDealer")}
            </Button>
          )}
        </Flex>
      </Flex>
      <ErrorBoundary notifTrigger={ErrorBoundaryNotification}>
        <TableV2
          wrapperClassName={s.tableWrapper}
          columns={columns}
          dataSource={dealers}
          loading={loading}
          sortField={sortField}
          sortType={normalizedSortType}
          sortableColumns={normalizedSortableColumns}
          onSortChange={onSortChange}
        />
        <Pagination
          count={count}
          page={page + 1}
          totalCount={total}
          onChangeCount={handleCount}
          onChangePage={handlePage}
        />
      </ErrorBoundary>

      <Modal
        isOpen={openModal === "htmlUpload"}
        title={t("dealers.htmlUpload")}
        actionClose={handleCloseModal}
      >
        <DealersHtmlFilesForm setCloseModal={handleCloseModal} />
      </Modal>
    </Wrapper.Content>
  );
};

export default Dealers;
